/**
 * Products API client · T-S1-A W7
 * 契约源：apps/api-server/src/api_server/routes/products.py
 */
import { api, fetchAllPages, ApiError } from "./api";
import type {
  ProductAvailability,
  ProductCreatePayload,
  ProductLegCreatePayload,
  ProductLegRead,
  ProductListQuery,
  ProductMediaItem,
  ProductMediaRead,
  ProductRead,
  ProductScheduleCreatePayload,
  ProductScheduleRead,
  ProductScheduleUpdatePayload,
  ProductStats,
  ProductUpdatePayload,
  ProductWithLegs,
  ScheduleGeneratePayload,
  ScheduleGenerateResult,
  ShareEmailPayload,
  ShareEmailResponse,
} from "./types/products";

function buildQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) usp.append(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

/** GET /api/v1/products/_stats */
export function getProductStats(): Promise<ProductStats> {
  return api("/api/v1/products/_stats");
}

/** GET /api/v1/products */
/** @param opts.paged GUARD · 调用方是带 UI 分页器的列表页时传 true（消除静默截断告警） */
export function listProducts(
  q?: ProductListQuery,
  opts?: { paged?: boolean },
): Promise<ProductRead[]> {
  const qs = buildQuery((q ?? {}) as Record<string, unknown>);
  return api(`/api/v1/products${qs}`, { paged: opts?.paged });
}

/**
 * GET /api/v1/products —— **翻完所有页**返回全量产品。
 *
 * 🔴 给"选产品"类下拉框用（下单向导 / 调度中心 / 派单向导）。后端 `limit` 默认 **50**、
 * 上限 200，且是静默截断（返回体不带 total，前端无从知道被截了）。default 租户 2026-07-30
 * 已有 80 个未删产品 → 无参 `listProducts()` 只拿到前 50 个（按 code 排序），
 * PRD-00048 之后的 30 个产品在下拉框里**根本不出现**，业务员会以为产品不存在。
 * （真实事故：同事手工下单找不到 PRD-00067 / PRD-00068。）
 *
 * 这里按页翻到底，不再依赖单页容量，产品再增长也不会重现。
 */
export function listAllProducts(q?: ProductListQuery): Promise<ProductRead[]> {
  return fetchAllPages((limit, offset) =>
    listProducts({ ...(q ?? {}), limit, offset } as ProductListQuery),
  );
}

/** POST /api/v1/products */
export function createProduct(payload: ProductCreatePayload): Promise<ProductWithLegs> {
  return api("/api/v1/products", { method: "POST", body: JSON.stringify(payload) });
}

/** GET /api/v1/products/{id} */
export function getProduct(id: string): Promise<ProductWithLegs> {
  return api(`/api/v1/products/${id}`);
}

/** PATCH /api/v1/products/{id} */
export function updateProduct(id: string, payload: ProductUpdatePayload): Promise<ProductRead> {
  return api(`/api/v1/products/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** DELETE /api/v1/products/{id} */
export function deleteProduct(id: string): Promise<void> {
  return api(`/api/v1/products/${id}`, { method: "DELETE" });
}

// ── Product Legs (编辑模式 leg 同步) ─────────────────────────────────────────
/** POST /api/v1/products/{id}/legs */
export function appendProductLeg(
  productId: string,
  payload: ProductLegCreatePayload,
): Promise<ProductLegRead> {
  return api(`/api/v1/products/${productId}/legs`, { method: "POST", body: JSON.stringify(payload) });
}

/** PATCH /api/v1/products/{id}/legs/{legId} */
export function updateProductLeg(
  productId: string,
  legId: string,
  payload: Partial<ProductLegCreatePayload>,
): Promise<ProductLegRead> {
  return api(`/api/v1/products/${productId}/legs/${legId}`, { method: "PATCH", body: JSON.stringify(payload) });
}

/** DELETE /api/v1/products/{id}/legs/{legId} */
export function deleteProductLeg(productId: string, legId: string): Promise<void> {
  return api(`/api/v1/products/${productId}/legs/${legId}`, { method: "DELETE" });
}

// CANCELPOL · 产品专属取消条款覆盖（NULL=继承统一条款）——独立端点，不走 updateProduct。
export interface CancellationPolicyOut { inherited: boolean; policy: { tiers: { days_before: number; refund_pct: number }[]; note?: string | null } }
/** GET /api/v1/products/{id}/cancellation-policy */
export function getProductCancellationPolicy(productId: string): Promise<CancellationPolicyOut> {
  return api(`/api/v1/products/${productId}/cancellation-policy`);
}
/** PUT /api/v1/products/{id}/cancellation-policy · policy=null 恢复继承 */
export function putProductCancellationPolicy(
  productId: string,
  policy: CancellationPolicyOut["policy"] | null,
): Promise<CancellationPolicyOut> {
  return api(`/api/v1/products/${productId}/cancellation-policy`, {
    method: "PUT",
    body: JSON.stringify({ policy }),
  });
}

// SITE-A · 图集（product_media 子表）——独立端点，不走 updateProduct（同 CANCELPOL 范式）。
/** GET /api/v1/products/{id}/media · LIST-1 · 带 width/height/platform_ok/platform_reason
 * （后端每次都算·老图无宽高懒补一次）——图集尺寸校验卡片用这个，不用详情内联的 product.media
 * （那个是 ORM 直出，没有 platform_ok/reason 这两个计算字段）。*/
export function getProductMedia(productId: string): Promise<ProductMediaRead[]> {
  return api(`/api/v1/products/${productId}/media`);
}
/** PUT /api/v1/products/{id}/media · 整表替换，顺序=数组顺序 */
export function putProductMedia(productId: string, items: ProductMediaItem[]): Promise<ProductMediaRead[]> {
  return api(`/api/v1/products/${productId}/media`, {
    method: "PUT",
    body: JSON.stringify({ items }),
  });
}

/** POST /api/v1/products/{id}/share-email · PUBPAGE 工单 · 公开行程页分享发送 */
export function shareProductEmail(productId: string, payload: ShareEmailPayload): Promise<ShareEmailResponse> {
  return api(`/api/v1/products/${productId}/share-email`, { method: "POST", body: JSON.stringify(payload) });
}

/** GET /api/v1/products/{id}/schedules?from=&to= */
export function listSchedules(
  productId: string,
  from?: string,
  to?: string,
  schedStatus?: string,
  optionId?: string | null,   // OPT-C2 · 只看这个选项的团期（不传 = 老行为）
): Promise<ProductScheduleRead[]> {
  const qs = buildQuery({ from, to, status: schedStatus, option_id: optionId ?? undefined });
  return api(`/api/v1/products/${productId}/schedules${qs}`);
}

/** POST /api/v1/products/{id}/schedules:generate — 按发团范围+频率批量铺排期 */
export function generateSchedules(
  productId: string,
  payload: ScheduleGeneratePayload,
): Promise<ScheduleGenerateResult> {
  return api(`/api/v1/products/${productId}/schedules:generate`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** POST /api/v1/products/{id}/schedules */
export function createSchedule(
  productId: string,
  payload: ProductScheduleCreatePayload,
): Promise<ProductScheduleRead> {
  return api(`/api/v1/products/${productId}/schedules`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** PATCH /api/v1/products/{id}/schedules/{sid} */
// ─── COSTADJ-1 · 团期级「本团成本」（只有 老板 / 财务 / 技术管理员；其它角色 403）──
export interface TourCostOrderRow {
  order_id: string; order_no: string; customer_name: string | null; source: string | null; status: string;
  pax_count: number; total_amount_cents: number; cost_price_cents: number | null; net_profit_cents: number | null;
  cost_basis: string | null; locked: boolean; net_before_cost_cents: number;
}
export interface TourCostView {
  product_id: string; schedule_id: string; departure_date: string;
  system_estimate_cents: number; override_cents: number | null;
  cohort_pax: number; cohort_order_count: number; revenue_cents: number; net_profit_cents: number;
  orders: TourCostOrderRow[];
  locked_orders: { order_id: string; order_no: string; cost_basis: string | null; status: string }[];
}
export interface TourCostPreview {
  schedule_id: string; system_estimate_cents: number; override_cents: number | null; amount_cents: number | null;
  cohort_pax: number; cohort_order_count: number; revenue_cents: number; per_person_cents: number | null;
  net_profit_after_cents: number;
  locked_orders: TourCostView["locked_orders"];
  orders: (TourCostOrderRow & { proposed_cost_cents: number; will_rewrite: boolean; cost_after_cents: number | null; net_after_cents: number | null })[];
}
const tourCostUrl = (productId: string, scheduleId: string) => `/api/v1/products/${productId}/schedules/${scheduleId}/tour-cost`;
export function getTourCost(productId: string, scheduleId: string): Promise<TourCostView> {
  return api(tourCostUrl(productId, scheduleId));
}
export function previewTourCost(productId: string, scheduleId: string, amountCents: number | null): Promise<TourCostPreview> {
  return api(`${tourCostUrl(productId, scheduleId)}:preview`, { method: "POST", body: JSON.stringify({ amount_cents: amountCents }) });
}
export function putTourCost(productId: string, scheduleId: string, amountCents: number | null): Promise<TourCostView> {
  return api(tourCostUrl(productId, scheduleId), { method: "PUT", body: JSON.stringify({ amount_cents: amountCents }) });
}

export function updateSchedule(
  productId: string,
  scheduleId: string,
  payload: ProductScheduleUpdatePayload,
): Promise<ProductScheduleRead> {
  return api(`/api/v1/products/${productId}/schedules/${scheduleId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** DELETE /api/v1/products/{id}/schedules/{sid} */
export function deleteSchedule(productId: string, scheduleId: string): Promise<void> {
  return api(`/api/v1/products/${productId}/schedules/${scheduleId}`, {
    method: "DELETE",
  });
}

/** GET /api/v1/products/{id}/availability?date=YYYY-MM-DD */
export function getProductAvailability(
  productId: string,
  dateStr: string,
): Promise<ProductAvailability> {
  return api(`/api/v1/products/${productId}/availability?date=${dateStr}`);
}

// ── TAPECHART · 产品运营总表聚合 ──────────────────────────────────────────────
export type TapeHealth = "green" | "amber" | "red" | "gray";

export interface TapeOrder {
  order_id: string;
  order_no: string;
  customer_name: string;
  pax_count: number;
  status: string;
  pickup_location: string | null;
  vehicle_id: string | null;
  vehicle_number: string | null;
  reminders: string[];
}
/** 方案B(老板拍板)·cohort 已派车辆聚合：chip + 跨行同车聚焦(套团) */
export interface TapeCohortVehicle {
  vehicle_id: string;
  vehicle_number: string;
  pax: number;
}
export interface TapeCohort {
  departure_date: string;
  schedule_id: string | null;
  schedule_status: string | null;
  max_pax: number | null;
  booked_pax: number;
  remaining: number | null;
  health: TapeHealth;
  has_reminder: boolean;
  status_breakdown: Record<string, number>;
  vehicles: TapeCohortVehicle[];
  orders: TapeOrder[];
}
export interface TapeProduct {
  product_id: string;
  name_cn: string;
  code: string;
  trip_days: number;
  cohorts: TapeCohort[];
}
export interface TapeChartResponse {
  from: string;
  to: string;
  products: TapeProduct[];
}

/** GET /api/v1/products/_tape-chart?from=&to= · 运营总表聚合（窗口≤62天）*/
export function getTapeChart(from: string, to: string): Promise<TapeChartResponse> {
  return api(`/api/v1/products/_tape-chart?from=${from}&to=${to}`);
}

// ── PRODHEALTH · 可售性体检（配置完整性·不是库存数值异常）───────────────────────
// 契约源：apps/api-server/src/api_server/services/product_health.py + routes/products.py
// 回答的是「这个上架产品窗口内真的能卖吗」——酒店/门票一行配置都没配时，既有告警引擎
// （WHERE 建立在"表里有行"上）看不见，只有确认订单那一步才会撞上。
export type ProductHealthVerdict = "ok" | "tight" | "blocked" | "no_schedule";

export interface ProductHealthIssue {
  severity: ProductHealthVerdict;
  resource: "hotel" | "ticket" | "vehicle" | "essentials" | "run"; // PUBGATE-1 · 上架必填三项；NORUN-1 · run = 缺车次
  title: string;
  fix_hint: string;
}

/** PUBGATE-1 · create/update 422 结构化 detail（契约源 routes/products.py + services/
 *  product_essentials.py）。ApiError.detail 是整个响应体 `{detail: {...}}`（见 services/api.ts
 *  rawFetch 的 `detail = body` 写法，同 inventory.ts::extractCostGuardMessage 的既有惯例），
 *  结构化 code 在 `detail.detail.code`，不是 `detail.code`。 */
export interface PublishEssentialsMissingError {
  code: "publish_essentials_missing";
  message: string;
  missing: string[];
}

/** 不是这个错误就返回 null，调用方落回原有的通用错误文案。 */
export function extractPublishEssentialsError(err: unknown): PublishEssentialsMissingError | null {
  if (!(err instanceof ApiError)) return null;
  const inner = (err.detail as { detail?: unknown } | undefined)?.detail;
  if (inner && typeof inner === "object" && (inner as PublishEssentialsMissingError).code === "publish_essentials_missing") {
    return inner as PublishEssentialsMissingError;
  }
  return null;
}

/** TIXFEE-1 · 单日保存有景点没选费用类型 → 422（契约源 routes/products.py::_build_attraction_meta）。
 *  结构化 code 在 `detail.detail.code`（同 PUBGATE-1 的写法）。 */
export interface AttractionFeeTypeRequiredError {
  code: "attraction_fee_type_required";
  message: string;
  missing: string[];
}

export function extractAttractionFeeTypeError(err: unknown): AttractionFeeTypeRequiredError | null {
  if (!(err instanceof ApiError)) return null;
  const inner = (err.detail as { detail?: unknown } | undefined)?.detail;
  // TIXSEL-1 · 选票相关 422（已含收费景点没选票 / 票不属于该景点 / 免费景点不用选票）同样是 {code, message}，一起接
  const code = inner && typeof inner === "object" ? (inner as AttractionFeeTypeRequiredError).code : undefined;
  if (code === "attraction_fee_type_required" || code === "attraction_ticket_required" || code === "attraction_ticket_invalid" || code === "attraction_ticket_not_needed") {
    return inner as AttractionFeeTypeRequiredError;
  }
  return null;
}

export interface ProductHealth {
  product_id: string;
  // 单产品端点在「窗口内无排期」分支只回 product_id/verdict/schedules_in_window/issues
  // （见 routes/products.py::product_health 的 fallback dict，没走 _health_payload）。
  code?: string;
  name_cn?: string;
  verdict: ProductHealthVerdict;
  schedules_in_window: number;
  /** NORUN-1 · 挂线路但近 30 天没有可挂车次（平台 / 同行站显示 0 位）；不挂线路 / 有车次 = false，老后端缺省 = 无 */
  no_run?: boolean;
  issues: ProductHealthIssue[];
}

export interface ProductsHealthResponse {
  date_from: string;
  date_to: string;
  total_checked: number;
  blocked: number;
  items: ProductHealth[];
}

/** GET /api/v1/products/health?date_from=&date_to=&only_problems=
 *  🔴 列表页/首页用这个拿全量一次性数据——绝不逐个产品调 getProductHealth。 */
export function getProductsHealth(
  dateFrom: string,
  dateTo: string,
  onlyProblems = true,
): Promise<ProductsHealthResponse> {
  const qs = buildQuery({ date_from: dateFrom, date_to: dateTo, only_problems: onlyProblems });
  return api(`/api/v1/products/health${qs}`);
}

/** GET /api/v1/products/{id}/health?date_from=&date_to= · 产品详情页体检条用 */
export function getProductHealth(productId: string, dateFrom: string, dateTo: string): Promise<ProductHealth> {
  const qs = buildQuery({ date_from: dateFrom, date_to: dateTo });
  return api(`/api/v1/products/${productId}/health${qs}`);
}
